import Application from './application';
import SegmentationPermission from './enums/segmentationPermission';
import Tag from './tag';

export interface AssignedClient {
  description?: string;
  displayName: string;
  email: string;
  phone: string;
  fName: string;
  lName: string;
  mName?: string;
  contactName: string;
  address1: string;
  address2?: string;
  city: string;
  state: number;
  stateName?: string;
  zipCode: string;
  status: boolean;
  brandColor?: string;
  clientId: number;
  name: string;
  supportEmail: string;
  enableServiceDesk: boolean;
  enableSegmentation: boolean;
  enableAutomaticPushes: boolean;
  brandImage: string;
}

export type AssignedTag = Omit<Tag, 'users'>;

/**
 * User object returned from the paginated user list endpoint
 */
export interface BaseUser {
  /** User ID */
  id: string;
  /** First name */
  firstName: string;
  /** Last name */
  lastName: string;
  /** Optional middle name */
  middleName?: string;
  /** Role: User, Client Admin or Elite Admin */
  role: string;
  /** Email address */
  email: string;
  /** A comma separated string of all access levels */
  accessLevel: string;
  /** Status of user from Okta*/
  status: UserOktaStatus | null;
  /** Enhanced data */
  enhancedData: boolean;
  /** Title */
  title: string;
  /** Department */
  department: string;
  /** Apps assigned to user, always null on list endpoint */
  assignedApps: null;
  /** Clients assigned to user, always null on list endpoint */
  assignedClients: null;
  /** Tags assigned to user, always null on list endpoint */
  assignedTags: null;
  /** Fan segmentation access per client */
  assignedSegmentation: {
    clientId: number;
    segmentationPermission: SegmentationPermission;
  }[];
  /** If account is federated */
  isFederated: boolean;
  activate?: boolean | null;
}

export enum UserOktaStatus {
  STAGED = 'Staged',
  PROVISIONED = '	Pending User Action',
  ACTIVE = 'Active',
  RECOVERY = '	Password Reset',
  PASSWORD_EXPIRED = 'Password Expired',
  LOCKED_OUT = 'Locked Out',
  SUSPENDED = 'Suspended',
  DEPROVISIONED = 'Deactivated'
}

/**
 * User
 * @interface User - Individual user object from API
 */
interface User extends Omit<BaseUser, 'assignedApps' | 'assignedClients' | 'assignedTags'> {
  /** Apps assigned to user, populated on single user endpoint */
  assignedApps: Application[];
  /** Clients assigned to user, populated on single user endpoint */
  assignedClients: AssignedClient[];
  /** Tags assigned to user, populated on single user endpoint */
  assignedTags: AssignedTag[];
}

export default User;
